import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormControlComponent } from '@forms/form-control/form-control.component';
import { ICountryCode } from '@shared/types/phone-country-codes';
import { parsePhoneNumber } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, skip, startWith, switchMap } from 'rxjs/operators';
import * as countryCodes from './countryCodes.json';

@Component({
  selector: 'rav-phone-number',
  templateUrl: './phone-number.component.html',
  styleUrls: ['./phone-number.component.scss']
})
export class PhoneNumberComponent extends FormControlComponent implements OnInit, OnDestroy {
  @ViewChild('countryFilterInput') countryFilterInput: ElementRef;
  @ViewChild('phoneNumberInput') phoneNumberInput: ElementRef;

  @Input() placeholder?: string;
  @Input() autocomplete = true;
  @Input() dataCy = '';
  @Input() controlFormGroup: FormGroup = null;
  @Input() controlName = '';
  @Input() countryCodeList = (countryCodes as any).default;

  countryFilterList = new FormControl();
  subscriptions: Subscription[] = [];
  selectedCountryCode: ICountryCode;
  isCountrySelectorOpen$: Observable<boolean>;
  filteredCountriesList$: Observable<ICountryCode[]>;
  phoneInputHasFocus$: Observable<boolean>;

  defaultCountryCode = {
    name: 'United States',
    twoLetterCode: 'us',
    countryCode: '+1'
  };
  selectedCountryCode$: BehaviorSubject<ICountryCode> = new BehaviorSubject<ICountryCode>(this.defaultCountryCode);
  private isCountrySelectorOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private phoneInputHasFocus: Subject<boolean> = new Subject<boolean>();

  constructor() {
    super();

    this.isCountrySelectorOpen$ = this.isCountrySelectorOpen.asObservable();
    this.phoneInputHasFocus$ = this.phoneInputHasFocus.asObservable();
  }

  ngOnInit() {
    // Add new form control to track selected country code
    this.controlFormGroup.addControl('country_code', new FormControl(''));

    // Subscriptions
    this.subscriptions.push(
      // Observable that holds currently selected ICountryCode
      this.selectedCountryCode$.subscribe((_selectedCountryCode) => {
        // Set selected country, so we can use those values on the template
        this.selectedCountryCode = _selectedCountryCode;
        // Patch selected country code to form group
        this.controlFormGroup.patchValue({ country_code: _selectedCountryCode });
      }),
      // Subscribe to know which input should be focused:
      //    - country filter input when autocomplete is open
      //    - phone number input when autocomplete is closed
      // Skip the first one because we know is the one we defined at the start
      // If we don't skip it, then phone number will be focused when the page loads for the first time
      this.isCountrySelectorOpen$.pipe(skip(1)).subscribe((isOpen) => {
        setTimeout(() => {
          if (isOpen) {
            this.countryFilterInput.nativeElement.focus();
            this.countryFilterInput.nativeElement.select();
          } else {
            this.phoneNumberInput.nativeElement.focus();
            this.countryFilterList.setValue('');
          }
        }, 0);
      }),
      this.controlFormGroup.valueChanges.subscribe((value) => {
        // If we don't have a value for `phone_number`, the form has not been touched and `country_code` selected is different from
        // the default means most likely the form is being reset, so, reset `country_code` to default
        if (
          (value[this.controlName] === '' || value[this.controlName] === null) &&
          !this.controlFormGroup.touched &&
          this.controlFormGroup.get(this.controlName).status !== 'INVALID' &&
          !isEqual(value['country_code'], this.defaultCountryCode)
        ) {
          this.selectedCountryCode$.next(this.defaultCountryCode);
          // Set filter list to show all values again
          this.countryFilterList.setValue('');
        }
      }),
      this.controlFormGroup.get(this.controlName).valueChanges.subscribe((value) => {
        // If the value received has more than 1 char, that means we received a previously saved value
        if (value && value.length > 1) {
          // Parse country from the phone number received
          try {
            const parsedPhoneNumber = parsePhoneNumber(value, <any>this.defaultCountryCode.twoLetterCode.toUpperCase());
            let countryISOCode;
            if (parsedPhoneNumber.country) {
              countryISOCode = parsedPhoneNumber.country.toString().toLowerCase();
            } else if (this.selectedCountryCode.twoLetterCode) {
              countryISOCode = this.selectedCountryCode.twoLetterCode.toUpperCase();
            } else {
              countryISOCode = this.defaultCountryCode.twoLetterCode.toUpperCase();
            }
            const selectedCountry = this.countryCodeList.filter(
              (country) => country.twoLetterCode.toLowerCase() === countryISOCode.toLowerCase()
            );
            if (selectedCountry.length > 0) {
              this.selectedCountryCode$.next(selectedCountry[0]);
              this.countryFilterList.setValue('');
            }
          } catch (e) {
            console.error('An error occurred when parsing received value', e);
            this.selectedCountryCode$.next(this.defaultCountryCode);
          }
        }
      })
    );

    const getCountries = (q) =>
      this.countryCodeList.filter((country) => {
        let r;
        const _q = typeof q === 'string' ? q : q.name;
        if (_q === '') {
          r = true;
        } else {
          r =
            country.name.toLowerCase().indexOf(_q.toLowerCase()) > -1 ||
            country.countryCode.indexOf(_q.toLowerCase()) > -1;
        }

        return r;
      });

    const countriesRequest$ = (q) => of(getCountries(q));

    this.filteredCountriesList$ = this.countryFilterList.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      switchMap(countriesRequest$)
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Method used to open autocomplete element
   */
  openCountryAutocomplete() {
    this.isCountrySelectorOpen.next(!this.isCountrySelectorOpen.getValue());
  }

  /**
   * Method used to display country name on `mat-autocomplete`
   *
   * @param selectedCountry Selected ICountryCode object
   */
  displaySelectedCountry(selectedCountry: ICountryCode) {
    return selectedCountry ? selectedCountry.name : '';
  }

  /**
   * Method used when a country is selected on the autocomplete
   */
  onCountrySelected(e: MatAutocompleteSelectedEvent) {
    this.selectedCountryCode$.next(e.option.value);
    this.isCountrySelectorOpen.next(false);
  }

  /**
   * Whenever the autocomplete panel closes, update `isCountrySelectorOpen` observable state
   */
  onCountryAutocompleteClosed(e) {
    this.isCountrySelectorOpen.next(false);
  }

  phoneNumberInputGotFocus(gotFocus: boolean) {
    this.phoneInputHasFocus.next(gotFocus);
  }
}
